import type { Product } from '../types';
import { getStoredUTMData } from '../utils/utmTracking';

type AnalyticsProperties = Record<string, unknown>;

type BrowserPostHog = {
  capture: (eventName: string, properties?: AnalyticsProperties) => void;
  get_session_id: () => string | undefined;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void;
  reset: () => void;
};

type PendingPostHogOperation =
  | { type: 'capture'; eventName: string; properties: AnalyticsProperties }
  | { type: 'identify'; distinctId: string; properties: AnalyticsProperties };

type ProductAnalyticsInput = Pick<
  Product,
  'id' | 'name' | 'price' | 'compare_at_price' | 'productCategoryId' | 'slug' | 'tags'
>;

type CartAnalyticsItem = ProductAnalyticsInput & { quantity: number };

const POSTHOG_TOKEN = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
const SESSION_ID_KEY = 'astroji_analytics_session_id';
const EVENT_NAME_PATTERN = /^[a-z_]+$/;
const MAX_PROPERTY_COUNT = 30;
const MAX_PENDING_OPERATIONS = 100;
const pendingOperations: PendingPostHogOperation[] = [];
const PII_KEYS = new Set([
  'address',
  'client_email',
  'client_phone',
  'contact',
  'customer_contact',
  'customer_email',
  'customer_name',
  'email',
  'full_address',
  'name',
  'name_for_consult',
  'phone',
  'shipping_address',
]);

const safeStorageGet = (storage: Storage, key: string) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (storage: Storage, key: string, value: string) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Analytics must not affect the primary user journey when storage is blocked.
  }
};

const getBrowserPostHog = (): BrowserPostHog | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as typeof window & { posthog?: BrowserPostHog }).posthog;
};

const queueOperation = (operation: PendingPostHogOperation) => {
  if (pendingOperations.length >= MAX_PENDING_OPERATIONS) pendingOperations.shift();
  pendingOperations.push(operation);
};

export const flushPostHogQueue = (client = getBrowserPostHog()) => {
  if (!client) return;

  let operation: PendingPostHogOperation | undefined;
  while ((operation = pendingOperations.shift())) {
    if (operation.type === 'capture') {
      client.capture(operation.eventName, operation.properties);
    } else {
      client.identify(operation.distinctId, operation.properties);
    }
  }
};

const createId = (prefix: string) => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return `${prefix}_${window.crypto.randomUUID()}`;
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
};

export const getSessionId = () => {
  if (typeof window === 'undefined') return 'server_session';
  const posthogSessionId = getBrowserPostHog()?.get_session_id();
  if (posthogSessionId) return posthogSessionId;

  const existing = safeStorageGet(sessionStorage, SESSION_ID_KEY);
  if (existing) return existing;

  const next = createId('sess');
  safeStorageSet(sessionStorage, SESSION_ID_KEY, next);
  return next;
};

export const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  if (window.matchMedia('(max-width: 767px)').matches) return 'mobile';
  if (window.matchMedia('(max-width: 1023px)').matches) return 'tablet';
  return 'desktop';
};

export const getPageCategory = (path = typeof window !== 'undefined' ? window.location.pathname : '/') => {
  if (path === '/') return 'home';
  if (path.startsWith('/product/')) return 'product';
  if (path.startsWith('/products')) return 'product_listing';
  if (path.startsWith('/cart')) return 'cart';
  if (path.startsWith('/checkout')) return 'checkout';
  if (path.startsWith('/order-success')) return 'order';
  if (path.startsWith('/consultation') || path.startsWith('/lp')) return 'consultation';
  if (path.startsWith('/track-order')) return 'order_tracking';
  if (path.startsWith('/sale')) return 'sale';
  return 'content';
};

const getPageName = (path = typeof window !== 'undefined' ? window.location.pathname : '/') => {
  if (path === '/') return 'home';
  return path
    .replace(/^\/+/, '')
    .replace(/\/+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .toLowerCase() || 'home';
};

const getAttributionProperties = () => {
  if (typeof window === 'undefined') return {};
  const utm = getStoredUTMData();
  if (!utm) return {};

  return {
    attribution_source: utm.source || utm.utm_source || null,
    attribution_campaign: utm.campaign || utm.utm_campaign || null,
    attribution_campaign_id: utm.campaign_id || utm.utm_id || null,
    attribution_campaign_name: utm.campaign_name || utm.utm_campaign || null,
    attribution_adset_id: utm.adset_id || null,
    attribution_adset_name: utm.adset_name || utm.adset || null,
    attribution_ad_id: utm.ad_id || null,
    attribution_ad_name: utm.ad_name || utm.ad || null,
    attribution_medium: utm.utm_medium || null,
    attribution_content: utm.ad || utm.utm_content || null,
    attribution_placement: utm.placement || utm.platform_position || null,
    attribution_site_source_name: utm.site_source_name || null,
    attribution_fbclid_present: Boolean(utm.fbclid),
    attribution_fbc_present: Boolean(utm.fbc),
    attribution_fbp_present: Boolean(utm.fbp),
    attribution_landing_page: utm.landing_page || null,
    attribution_captured_at: utm.captured_at || null,
  };
};

const getBaseProperties = (): AnalyticsProperties => ({
  session_id: getSessionId(),
  page_name: getPageName(),
  page_path: typeof window !== 'undefined' ? window.location.pathname : '/',
  page_category: getPageCategory(),
  device_type: getDeviceType(),
  event_source: 'frontend',
  ...getAttributionProperties(),
});

const sanitizeProperties = (properties: AnalyticsProperties = {}) => {
  const sanitized: AnalyticsProperties = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined) return;

    if (PII_KEYS.has(key.toLowerCase())) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[PostHog DEV]: Dropped PII property "${key}"`);
      }
      return;
    }

    sanitized[key] = value;
  });

  return sanitized;
};

const validateEventName = (eventName: string) => {
  if (!EVENT_NAME_PATTERN.test(eventName) || eventName.length > 50) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[PostHog DEV]: Invalid event name "${eventName}"`);
    }
    return false;
  }
  return true;
};

export const trackEvent = (eventName: string, properties: AnalyticsProperties = {}) => {
  if (!POSTHOG_TOKEN || !validateEventName(eventName)) return;

  const eventProperties = {
    ...getBaseProperties(),
    ...sanitizeProperties(properties),
  };

  if (Object.keys(eventProperties).length > MAX_PROPERTY_COUNT && process.env.NODE_ENV === 'development') {
    console.warn(`[PostHog DEV]: "${eventName}" has ${Object.keys(eventProperties).length} properties`);
  }

  const client = getBrowserPostHog();
  if (client) client.capture(eventName, eventProperties);
  else queueOperation({ type: 'capture', eventName, properties: eventProperties });
};

export const trackPageViewed = (properties: AnalyticsProperties = {}) => {
  trackEvent('page_viewed', {
    page_title: typeof document !== 'undefined' ? document.title : '',
    path_query: typeof window !== 'undefined' ? (window.location.search || null) : null,
    ...properties,
  });
};

const hashIdentifierFallback = (value: string) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `customer_${(hash >>> 0).toString(36)}`;
};

export const identifyCustomer = async (customerId: string) => {
  if (!POSTHOG_TOKEN || !customerId) return;

  let distinctId = hashIdentifierFallback(customerId);
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const digest = await window.crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(customerId),
    );
    distinctId = `customer_${Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('')}`;
  }

  const properties = { customer_type: 'authenticated' };
  const client = getBrowserPostHog();
  if (client) client.identify(distinctId, properties);
  else queueOperation({ type: 'identify', distinctId, properties });
};

export const resetAnalytics = () => {
  if (!POSTHOG_TOKEN) return;
  const client = getBrowserPostHog();
  if (client) client.reset();
  else pendingOperations.length = 0;
};

export const getProductAnalytics = (
  product: ProductAnalyticsInput,
  extra: AnalyticsProperties = {},
) => {
  const price = Number(product.price || 0);
  const compareAtPrice = Number(product.compare_at_price || 0);
  const tagsObj = product.tags;
  const tags: string[] = Array.isArray(tagsObj)
    ? tagsObj
    : [
        ...(tagsObj?.category_tags || []),
        ...(tagsObj?.purpose_tags || []),
        ...(tagsObj?.generic_tags || []),
      ];
  const discountPct = compareAtPrice > price && compareAtPrice > 0
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : 0;

  return {
    product_id: product.id,
    product_name: product.name,
    product_slug: product.slug,
    product_price: price,
    product_compare_at_price: compareAtPrice || null,
    category: product.productCategoryId || 'unknown',
    discount_pct: discountPct,
    is_rudraksha_included:
      product.name.toLowerCase().includes('rudraksha') ||
      tags.some((tag) => tag.toLowerCase().includes('rudraksha') || tag.toLowerCase() === 'free'),
    is_free_product: tags.some((tag) => tag.toLowerCase() === 'free'),
    ...extra,
  };
};

export const getCartAnalytics = (items: CartAnalyticsItem[]) => ({
  cart_item_count: items.reduce((total, item) => total + item.quantity, 0),
  cart_unique_items_count: items.length,
  cart_total_value_rs: Math.round(items.reduce(
    (total, item) => total + Number(item.price || 0) * item.quantity,
    0,
  ) * 100) / 100,
  has_free_gift: items.some((item) => {
    const tagsObj = item.tags;
    const tags: string[] = Array.isArray(tagsObj)
      ? tagsObj
      : [
          ...(tagsObj?.category_tags || []),
          ...(tagsObj?.purpose_tags || []),
          ...(tagsObj?.generic_tags || []),
        ];
    return tags.some((tag) => tag.toLowerCase() === 'free');
  }),
  cart_product_ids: items.map((item) => item.id),
});

export const trackProductViewed = (product: ProductAnalyticsInput, properties: AnalyticsProperties = {}) =>
  trackEvent('product_viewed', getProductAnalytics(product, properties));

export const trackAddToCartClicked = (product: ProductAnalyticsInput, properties: AnalyticsProperties = {}) =>
  trackEvent('add_to_cart_clicked', getProductAnalytics(product, properties));

export const trackAddToCartCompleted = (product: ProductAnalyticsInput, properties: AnalyticsProperties = {}) =>
  trackEvent('add_to_cart_completed', getProductAnalytics(product, properties));
